// Placement for fixed-position floating UI (tooltips, popovers) that must stay clear of
// "keep-out" rects — the Twitch player iframe above all: in Chrome its IntersectionObserver
// v2 (`trackVisibility`) reports the player as obscured as soon as anything is painted over
// it, and the SDK pauses playback. Pure, no DOM access — tools/test-floating-position.mjs.

export interface FloatRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export type FloatSide = 'top' | 'bottom' | 'right' | 'left';

export interface FloatPlacement {
  left: number;
  top: number;
  side: FloatSide;
}

const intersectionArea = (a: FloatRect, b: FloatRect): number => {
  const w = Math.min(a.right, b.right) - Math.max(a.left, b.left);
  const h = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  return w > 0 && h > 0 ? w * h : 0;
};

const rectAt = (left: number, top: number, width: number, height: number): FloatRect =>
  ({ left, top, right: left + width, bottom: top + height });

/**
 * Tries the sides in `order`. Each candidate is clamped into the viewport along its free
 * axis; if it then hits a keep-out rect it is pushed past that rect along the same axis
 * (top/bottom slide horizontally, left/right slide vertically). First candidate that fits
 * the viewport without touching any keep-out rect wins; otherwise the one with the least
 * overlap does.
 */
export function placeFloating(
  anchor: FloatRect,
  size: { width: number; height: number },
  viewport: { width: number; height: number },
  order: FloatSide[],
  keepOut: FloatRect[] = [],
  gap = 8,
  pad = 8,
): FloatPlacement {
  const { width: w, height: h } = size;
  const maxLeft = Math.max(pad, viewport.width - w - pad);
  const maxTop = Math.max(pad, viewport.height - h - pad);
  const clampX = (x: number) => Math.min(maxLeft, Math.max(pad, x));
  const clampY = (y: number) => Math.min(maxTop, Math.max(pad, y));
  const fitsViewport = (r: FloatRect) =>
    r.left >= pad - 0.5 && r.top >= pad - 0.5
    && r.right <= viewport.width - pad + 0.5 && r.bottom <= viewport.height - pad + 0.5;
  const overlap = (r: FloatRect) => keepOut.reduce((sum, k) => sum + intersectionArea(r, k), 0);
  const viewportRect = rectAt(0, 0, viewport.width, viewport.height);

  let best: { placement: FloatPlacement; score: number } | null = null;

  for (const side of order) {
    let left: number;
    let top: number;
    if (side === 'top' || side === 'bottom') {
      top = side === 'top' ? anchor.top - h - gap : anchor.bottom + gap;
      left = clampX((anchor.left + anchor.right) / 2 - w / 2);
    } else {
      left = side === 'right' ? anchor.right + gap : anchor.left - w - gap;
      top = clampY((anchor.top + anchor.bottom) / 2 - h / 2);
    }

    // Slide along the free axis past a keep-out rect — but only as far as the floating box
    // still spans the anchor's center on that axis. Otherwise a tooltip for a pin in the
    // middle of the heatmap would "fit" by jumping into the sidebar, far from its pin;
    // better to fail this side and let the next one (e.g. below the pin) win.
    const cx = (anchor.left + anchor.right) / 2;
    const cy = (anchor.top + anchor.bottom) / 2;
    let rect = rectAt(left, top, w, h);
    for (const k of keepOut) {
      if (intersectionArea(rect, k) === 0) continue;
      // Options that fit the viewport first, then the closer one; one poking past the window
      // edge is still kept — the fallback below prefers it over covering the keep-out rect.
      const rank = (fits: boolean, dist: number) => (fits ? 0 : 1e9) + dist;
      if (side === 'top' || side === 'bottom') {
        const options = [k.left - gap - w, k.right + gap]
          .filter(x => cx >= x && cx <= x + w)
          .sort((a, b) =>
            rank(fitsViewport(rectAt(a, top, w, h)), Math.abs(a - left))
            - rank(fitsViewport(rectAt(b, top, w, h)), Math.abs(b - left)));
        if (options.length) left = options[0];
      } else {
        const options = [k.top - gap - h, k.bottom + gap]
          .filter(y => cy >= y && cy <= y + h)
          .sort((a, b) =>
            rank(fitsViewport(rectAt(left, a, w, h)), Math.abs(a - top))
            - rank(fitsViewport(rectAt(left, b, w, h)), Math.abs(b - top)));
        if (options.length) top = options[0];
      }
      rect = rectAt(left, top, w, h);
    }

    const placement: FloatPlacement = { left, top, side };
    if (overlap(rect) === 0 && fitsViewport(rect)) return placement;

    // Nothing fit cleanly. Score this side as-is and clamped into the viewport; covering
    // a keep-out rect weighs far more than poking past the window edge — a half-visible
    // tooltip beats a paused player.
    const clamped: FloatPlacement = { left: clampX(left), top: clampY(top), side };
    for (const p of [placement, clamped]) {
      const r = rectAt(p.left, p.top, w, h);
      const score = overlap(r) * 1e6 + (w * h - intersectionArea(r, viewportRect));
      if (!best || score < best.score) best = { placement: p, score };
    }
  }

  return best ? best.placement : { left: pad, top: pad, side: order[0] ?? 'top' };
}
